'use client';
import React, { useState, useEffect, useMemo } from 'react';
import { TEvaluationModel, TEvaluationModelProvider, TModelDeveloper } from '@/lib/evaluationModelsApi';
import uniqBy from 'lodash-es/uniqBy';

const getModelsForType = (availableModels: TEvaluationModel[], inputType: string, outputType: string) => {
  return availableModels
    .filter((model) => model.capabilities.output.includes(outputType))
    .filter((model) => model.capabilities.input.includes(inputType));
};

type TUseAvailableModelsProps = {
  allModels: TEvaluationModel[];
  initialInputType: string;
  initialOutputType: string;
  providerId: string;
  developerId: string;
};

const useAvailableModels = ({
  allModels,
  initialInputType,
  initialOutputType,
  providerId,
  developerId,
}: TUseAvailableModelsProps) => {
  const allProviders = uniqBy(
    allModels.map((model) => model.provider),
    'id',
  ).filter((p) => p !== null);
  const allDevelopers = uniqBy(
    allModels.map((model) => model.developer),
    'id',
  ).filter((d) => d !== null);

  const initialModels = useMemo(
    () => getModelsForType(allModels, initialInputType, initialOutputType),
    [allModels, initialInputType, initialOutputType],
  );
  const [inputType, setInputType] = useState<string>(initialInputType);
  const [outputType, setOutputType] = useState<string>(initialOutputType);
  const [typeModels, setTypeModels] = useState<TEvaluationModel[]>(initialModels);
  const [provider, setProvider] = useState<TEvaluationModelProvider | undefined>(
    allProviders.find((p) => p.id === providerId),
  );

  const [developer, setDeveloper] = useState<TModelDeveloper | undefined>(
    allDevelopers.find((d) => d?.id === developerId) || undefined,
  );

  useEffect(() => {
    const modelsForType = getModelsForType(allModels, inputType, outputType);
    setTypeModels(modelsForType);
  }, [inputType, outputType]);

  const providers = useMemo(
    () =>
      uniqBy(
        typeModels.map((model) => model.provider),
        'id',
      ).filter((p) => p !== null),
    [typeModels],
  );

  const developers = useMemo(
    () =>
      uniqBy(
        typeModels.map((model) => model.developer),
        'id',
      ).filter((d) => d !== null),
    [typeModels],
  );

  const providerModels = useMemo(
    () => (provider ? typeModels.filter((model) => model.provider.id === provider.id) : typeModels),
    [provider, typeModels],
  );

  const developerModels = useMemo(
    () => (developer ? typeModels.filter((model) => model.developer?.id === developer.id) : typeModels),
    [developer, typeModels],
  );

  return {
    inputType,
    setInputType,
    outputType,
    setOutputType,
    provider,
    setProvider,
    providers,
    developer,
    setDeveloper,
    developers,
    models: developerModels || providerModels,
  };
};

export default useAvailableModels;
